import { Fragment, type ReactElement, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { ConditionCard, StyledConditionSeparator } from "app/pages/sales-intelligence/common-components/filters-panel";
import type { DynamicFilterGroupValue } from "app/pages/sales-intelligence/domain/search/types/dynamic-filter-values";
import { operatorOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import { createDynamicGroupFilterViewModel } from "../../../models/view/dynamic-group-filter/factory";

import { ConditionModal } from "./ConditionModal";
import type { GroupViewProps } from "./types";

const DEFAULT_VALUE: DynamicFilterGroupValue = {};

/**
 * Result-view half of the compound (per-condition) group filter (SI-8738): one always-visible {@link ConditionCard}
 * per active condition (a CRM field with its own include/exclude and OR-joined values), separated by "AND", and the
 * edit dialog opened from a card's pencil. New conditions are added from the filter view's "+ Add condition" trigger
 * ({@link DynamicGroupFilterConditionsView}). Presentation is derived through the group view model.
 */
export const DynamicGroupFilterConditionsResultView = ({
    filter,
    value = DEFAULT_VALUE,
    isActionEnabled = true,
    onFilterUpdate,
}: GroupViewProps): ReactElement | null => {
    const { translate } = useTranslate();

    const [editFieldKey, setEditFieldKey] = useState<string | undefined>(undefined);

    const { conditions } = useMemo(() => createDynamicGroupFilterViewModel({ filter, value }), [filter, value]);

    const removeCondition = (key: string): void => {
        const next: DynamicFilterGroupValue = { ...value };

        delete next[key];

        onFilterUpdate(filter.isValueEmpty(next) ? undefined : next);
    };

    const applyEdit = (next?: DynamicFilterGroupValue): void => {
        onFilterUpdate(next);
        setEditFieldKey(undefined);
    };

    if (conditions.length === 0) {
        return null;
    }

    return (
        <div data-automation="dynamic-group-conditions-result">
            {conditions.map((condition, index) => (
                <Fragment key={condition.key}>
                    {index > 0 ? <StyledConditionSeparator>{operatorOptionMap.and}</StyledConditionSeparator> : null}

                    <ConditionCard
                        fullWidth
                        hideActions={!isActionEnabled}
                        dataAutomation={`dynamic-group-condition-${condition.key}`}
                        inclusionLabel={translate(condition.inclusionLabelKey)}
                        fieldLabel={condition.fieldLabel}
                        values={condition.valueLabels}
                        onEdit={() => setEditFieldKey(condition.key)}
                        onDelete={() => removeCondition(condition.key)}
                    />
                </Fragment>
            ))}

            <ConditionModal
                isOpen={editFieldKey !== undefined}
                filter={filter}
                value={value}
                initialFieldKey={editFieldKey}
                onApply={applyEdit}
                onClose={() => setEditFieldKey(undefined)}
            />
        </div>
    );
};
